import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  cancelEmbeddedSignup,
  finalizeEmbeddedSignup,
} from "../services/metaEmbeddedSignupApi";

function postCallbackMessage(payload) {
  if (!window.opener || window.opener.closed) {
    return;
  }

  try {
    window.opener.postMessage(
      {
        type: "meta-embedded-signup-callback",
        ...payload,
      },
      window.location.origin
    );
  } catch (error) {
    // Ignore cross-window messaging failures and keep the callback page usable.
  }
}

function buildSessionEvent(event, extraData = {}) {
  return {
    type: "WA_EMBEDDED_SIGNUP",
    event,
    data: {
      current_step: "react_callback_page",
      callback_transport: "react_route",
      callback_path: window.location.pathname,
      callback_search: window.location.search,
      ...extraData,
    },
    client_context: {
      transport: "react_route_callback",
      received_at: new Date().toISOString(),
    },
  };
}

export default function MetaEmbeddedSignupCallbackPage() {
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const sessionKey = searchParams.get("state")?.trim() || "";
  const code = searchParams.get("code")?.trim() || "";
  const errorCode = searchParams.get("error_code")?.trim() || "";
  const error = searchParams.get("error")?.trim() || "";
  const errorMessage = (
    searchParams.get("error_message")
    || searchParams.get("error_description")
    || ""
  ).trim();

  const [status, setStatus] = useState("working");
  const [message, setMessage] = useState("Finishing the Meta signup callback and syncing your WhatsApp line.");
  const [details, setDetails] = useState("");
  const closeTimerRef = useRef(null);

  useEffect(() => () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }
  }, []);

  useEffect(() => {
    let isActive = true;

    const queueWindowClose = () => {
      closeTimerRef.current = window.setTimeout(() => {
        try {
          window.close();
        } catch (closeError) {
          // Some browsers ignore script-initiated closes after OAuth redirects.
        }
      }, 1200);
    };

    const completeWithStatus = (nextStatus, nextMessage, nextDetails = "", messagePayload = null) => {
      if (!isActive) {
        return;
      }

      setStatus(nextStatus);
      setMessage(nextMessage);
      setDetails(nextDetails);

      if (messagePayload) {
        postCallbackMessage(messagePayload);
      }

      if (nextStatus === "success" || nextStatus === "warning") {
        queueWindowClose();
      }
    };

    const run = async () => {
      if (!sessionKey) {
        completeWithStatus(
          "error",
          "The Meta callback did not include a signup session key.",
          "Return to the original WhatsApp Lines tab and start the connection again.",
          {
            status: "error",
            sessionKey: null,
            message: "Meta callback arrived without a session key.",
          }
        );
        return;
      }

      if (error) {
        try {
          await cancelEmbeddedSignup({
            session_key: sessionKey,
            session_event: buildSessionEvent("ERROR", {
              error,
              error_code: errorCode || null,
              error_message: errorMessage || null,
            }),
          });
        } catch (requestError) {
          // Even if cancellation update fails, surface the Meta error clearly.
        }

        completeWithStatus(
          "warning",
          "The Meta signup flow was cancelled or returned an error before completion.",
          errorMessage || error,
          {
            status: "cancelled",
            sessionKey,
            message: errorMessage || error || "Meta signup was cancelled.",
          }
        );
        return;
      }

      if (!code) {
        completeWithStatus(
          "error",
          "Meta redirected back without an authorization code.",
          "Return to the original WhatsApp Lines tab and try the connection again.",
          {
            status: "error",
            sessionKey,
            message: "Meta callback returned without an authorization code.",
          }
        );
        return;
      }

      if (window.opener && !window.opener.closed) {
        setStatus("working");
        setMessage("Authorization code received. Completing the signup in the original WhatsApp Lines tab.");
        setDetails("");
        postCallbackMessage({
          status: "code_received",
          sessionKey,
          code,
        });
        queueWindowClose();
        return;
      }

      try {
        const result = await finalizeEmbeddedSignup({
          session_key: sessionKey,
          code,
          session_event: buildSessionEvent("FINISH"),
        });

        completeWithStatus(
          "success",
          "The WhatsApp Business number was connected successfully.",
          result?.line?.display_name
            ? `Saved line: ${result.line.display_name}`
            : "The original tab will refresh the linked line automatically.",
          {
            status: "success",
            sessionKey,
            line: result?.line ?? null,
          }
        );
      } catch (requestError) {
        const failureMessage =
          requestError?.response?.data?.message
          || requestError?.message
          || "The callback reached the app, but the WhatsApp line could not be finalized.";

        completeWithStatus(
          "error",
          "The Meta signup callback could not be completed.",
          failureMessage,
          {
            status: "error",
            sessionKey,
            message: failureMessage,
          }
        );
      }
    };

    run().catch((unexpectedError) => {
      if (!isActive) {
        return;
      }

      const failureMessage = unexpectedError?.message || "Unexpected callback error.";

      setStatus("error");
      setMessage("The Meta signup callback could not be completed.");
      setDetails(failureMessage);
      postCallbackMessage({
        status: "error",
        sessionKey,
        message: failureMessage,
      });
    });

    return () => {
      isActive = false;
    };
  }, [code, error, errorCode, errorMessage, sessionKey]);

  const severity =
    status === "success" ? "success" : status === "warning" ? "warning" : status === "error" ? "error" : "info";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        bgcolor: "#f5f7fb",
      }}
    >
      <Paper elevation={6} sx={{ width: "100%", maxWidth: 560, borderRadius: 4, p: 4 }}>
        <Stack spacing={2.5}>
          <Typography variant="h5" fontWeight={800}>
            Meta Embedded Signup
          </Typography>

          {status === "working" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body1">{message}</Typography>
            </Box>
          ) : (
            <Alert severity={severity}>
              <Typography variant="body1" fontWeight={700}>
                {message}
              </Typography>
              {details ? (
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                  {details}
                </Typography>
              ) : null}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            Keep the original WhatsApp Lines tab open. It will refresh the signup session automatically.
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={() => window.close()}>
              Close window
            </Button>
            <Button variant="outlined" href="/">
              Return to app
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
